/* *
 * The hook is for theme partial or deep partial update.
 * Will overrwite the entire app theme, if another theme is passed with setter.
 * */
import { useEffect, useState } from 'react';
import merge from 'deepmerge';
import { ITheme } from '../Themes/interfaces';

export type DeepPartialTheme<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? DeepPartialTheme<U>[]
    : T[P] extends Readonly<infer U>[]
    ? Readonly<DeepPartialTheme<U>>[]
    : DeepPartialTheme<T[P]>;
};

type ThemeStateListener = () => void;
type UseThemeStateResult = [ITheme, (next: DeepPartialTheme<ITheme>, overwrite?: boolean) => void];
type CreateThemeResult = () => UseThemeStateResult;

/* *
 * Initialize hook with default theme.
 * Store all the subscribers, and call them on theme change.
 * Return useTheme as a global hook, for futher access within an app.
 * */
const createTheme = (defaultTheme: ITheme): CreateThemeResult => {
  let theme = defaultTheme;
  const stateListeners = new Set<ThemeStateListener>();

  const setThemeState = (next: DeepPartialTheme<ITheme>, overwrite = false): void => {
    theme = overwrite ? (next as ITheme) : (merge(theme, next) as ITheme);
    stateListeners.forEach((listener: ThemeStateListener) => listener());
  };

  const useThemeState = (): UseThemeStateResult => {
    const [state, setState] = useState(theme);

    useEffect(() => {
      const stateListener: ThemeStateListener = () => setState(theme);
      stateListeners.add(stateListener);
      return () => {
        stateListeners.delete(stateListener);
      };
    }, []);

    return [state, setThemeState];
  };
  return useThemeState;
};

let themeState: CreateThemeResult;

/* *
 * Initialize theme once, make it available to a consumer through useTheme.
 * USAGE:
 * 1. Must be initialized with theme arg at the root level e.g. const [theme] = useTheme(themeCmcLight);
 * LATER IN THE APP.
 * 1. const [theme, setTheme] = useTheme(); (full case).
 * 2. const [, setTheme] = useTheme(); (only set case).
 * In case if theme requires complete overwrite (not merge), use setTheme with optional replaceTheme flag set to 'true'.
 * */
export const useTheme = (initialTheme?: ITheme): UseThemeStateResult => {
  if (!initialTheme && !themeState) {
    throw new Error(`Error: Initial theme is not implemented!`);
  }

  if (initialTheme && !themeState) {
    themeState = createTheme(initialTheme);
  }

  return themeState();
};
